import { api } from '../config/api';
import {
  HISTORY_PAGE_SIZE,
  PATHS,
  normalizeWheelList,
  normalizeWinnersPayload,
  serverNowOf,
  wheelFromPayload,
} from '../components/prizeWheelShape';

// ---- The brand's prize wheels ("Szerencsekerék", the Challenges page) ----
// The contract — paths, fields, error codes — lives in components/prizeWheelShape.js.

function query(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

const withClock = (payload) => ({ wheel: wheelFromPayload(payload), serverNowMs: serverNowOf(payload) });

/**
 * The brand's wheels, newest first.
 * @param {string} brandId
 * @param {{ status?: string, limit?: number, before?: string|null }} [params]
 */
export async function fetchPrizeWheels(brandId, { status, limit = HISTORY_PAGE_SIZE, before } = {}) {
  const payload = await api(`${PATHS.list(brandId)}${query({ status, limit, before })}`);
  return normalizeWheelList(payload);
}

/** Schedule a wheel. `body` comes from validateWheelForm (title, prizeDescription, winnerCount, opensAt?, drawAt, rulesAccepted). */
export async function createPrizeWheel(brandId, body) {
  return withClock(await api(PATHS.create(brandId), { method: 'POST', body }));
}

/** Edit a scheduled wheel nobody has joined yet: { title?, prizeDescription?, drawAt? }. */
export async function updatePrizeWheel(brandId, id, body) {
  return withClock(await api(PATHS.one(brandId, id), { method: 'PATCH', body }));
}

export async function cancelPrizeWheel(brandId, id) {
  return withClock(await api(PATHS.cancel(brandId, id), { method: 'POST', body: {} }));
}

export async function fetchPrizeWheelWinners(brandId, id) {
  return normalizeWinnersPayload(await api(PATHS.winners(brandId, id)));
}

// ---- Starting the draw (contract v2: the brand presses "Sorsolás indítása") ----
// THE ONE PLACE the draw-now wire lives. Over the brand's portal socket (the
// /style-universe gift:brand room, permission-checked by the server) with an
// ack; the API agent finalises the name — assumed `prize_wheel_draw_now`
// { wheelId } → ack { ok: true, prizeWheel? } | { ok: false, error: '<code>' }.
// Without a socket (the Shopify embedded app, or a dropped connection) it falls
// back to POST …/:wheelId/draw (assumed). If nobody presses, the server draws
// by itself 30 s after drawAt.
export const DRAW_NOW_EVENT = 'prize_wheel_draw_now';
export const DRAW_NOW_ACK_TIMEOUT_MS = 8000;

/**
 * @param {{ brandId: string, wheelId: string, socket?: { connected: boolean, emitWithAck: Function } | null }} args
 * @returns {Promise<{ wheel: object|null, serverNowMs: number|null }>}
 */
export async function startPrizeWheelDraw({ brandId, wheelId, socket = null }) {
  if (socket && socket.connected) {
    const ack = await socket.emitWithAck(DRAW_NOW_EVENT, { wheelId }, DRAW_NOW_ACK_TIMEOUT_MS);
    if (ack && (ack.ok === false || ack.error)) {
      const code = typeof ack.error === 'string' ? ack.error : ack.code ?? 'prize_wheel_draw_failed';
      const err = new Error(code);
      err.status = ack.status ?? 409;
      err.data = { message: code, details: ack.details ?? null };
      throw err;
    }
    return withClock(ack ?? {});
  }
  return withClock(await api(PATHS.drawNow(brandId, wheelId), { method: 'POST', body: {} }));
}
